import { useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import lightGallery from 'lightgallery';
import { motion } from 'framer-motion';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import ResponsiveImage from './ResponsiveImage';
import { useSiteData } from '../context/SiteDataContext';
import { getImageUrl, getLargeImageUrl } from '../lib/supabase';
import 'swiper/css';
import 'swiper/css/navigation';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-zoom.css';

function Gallery() {
  const { gallery, loading } = useSiteData();
  const galleryRef = useRef(null);

useEffect(() => {
  if (loading || !gallery?.length) return;
  const lg = lightGallery(galleryRef.current, {
  selector: '.lightbox-item',
  plugins: [lgZoom],
  licenseKey: '0000-0000-000-0000',
  speed: 200,
  crossOrigin: 'anonymous',
  fetchSubHtml: false,
  hideScrollbar: true,
  loop: true,
  preload: 0,           // don't preload adjacent slides
  backdropDuration: 150,
  hideDelay: 0,
  animateThumb: false,
  zoomFromOrigin: false,
  allowMediaOverlap: false,
  loadYoutubeThumbnail: false,
});
  return () => lg.destroy();
}, [loading, gallery]);

  const images = gallery ?? [];

  return (
    <section
      id="gallery"
      className="modern-gallery py-5"
      style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', position: 'relative' }}
    >
      <div className="position-absolute top-0 start-0 w-100 h-100 overflow-hidden" style={{ zIndex: 1 }}>
        <div className="position-absolute rounded-circle" style={{ width: '160px', height: '160px', background: 'linear-gradient(45deg, rgba(66, 89, 168, 0.1), rgba(46, 141, 215, 0.1))', top: '15%', right: '10%', filter: 'blur(20px)' }} />
        <div className="position-absolute rounded-circle" style={{ width: '200px', height: '200px', background: 'linear-gradient(45deg, rgba(46, 141, 215, 0.08), rgba(66, 89, 168, 0.08))', bottom: '20%', left: '5%', filter: 'blur(25px)' }} />
      </div>

      <motion.div className="container text-center position-relative" style={{ zIndex: 2 }} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 1, ease: 'easeOut' }}>
        <div className="mb-5">
          <h2 className="section-heading text-uppercase responsive-heading fw-bold mb-4" style={{ color: '#4259A8', fontSize: '2.5rem', letterSpacing: '2px' }}>Galerija</h2>
          <div className="mx-auto mb-4" style={{ width: '80px', height: '4px', background: 'linear-gradient(90deg, #4259A8, #2E8DD7)', borderRadius: '2px' }} />
        </div>
      </motion.div>

      <motion.div className="swiper container position-relative" ref={galleryRef} style={{ zIndex: 2 }} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}>
        <div className="gallery-wrapper p-4 rounded-3" style={{ background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.9))', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 8px 32px rgba(66, 89, 168, 0.1)' }}>
          <Swiper
            modules={[Navigation]}
            navigation={{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }}
            slidesPerView={1}
            spaceBetween={16}
            breakpoints={{ 1025: { slidesPerView: 3 } }}
          >
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                <a       
  href={getLargeImageUrl(image.filename)}
  data-src={getLargeImageUrl(image.filename)}
  data-thumb={getImageUrl(image.filename)}
  data-lg-size="1600x1067"
  className="lightbox-item"
                >
                  <div
                    className="gallery-image-wrapper position-relative overflow-hidden rounded-3"
                    style={{ transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)', boxShadow: '0 4px 15px rgba(66, 89, 168, 0.15)', border: '2px solid rgba(255, 255, 255, 0.3)' }}
                  >
                    <ResponsiveImage
                      className="galery-image w-100"
                      src={getImageUrl(image.filename)}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 33vw"
                      alt={image.alt}
                      objectFit="cover"
                      style={{ height: '300px', transition: 'transform 0.4s ease' }}
                    />
                  </div>
                </a>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="swiper-button-next custom-button d-flex align-items-center justify-content-center rounded-circle" style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #4259A8 0%, #2E8DD7 100%)', color: 'white', border: 'none', boxShadow: '0 4px 15px rgba(66, 89, 168, 0.3)', right: '10px', marginTop: 0, transform: 'translateY(-50%)' }} />
          <div className="swiper-button-prev custom-button d-flex align-items-center justify-content-center rounded-circle" style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #4259A8 0%, #2E8DD7 100%)', color: 'white', border: 'none', boxShadow: '0 4px 15px rgba(66, 89, 168, 0.3)', left: '10px', marginTop: 0, transform: 'translateY(-50%)' }} />
        </div>
      </motion.div>
    </section>
  );
}

export default Gallery;